import z from "zod";

import { isDateWithinTextingHours } from "./common";

export enum CriteriaType {
  VARIANT_REACHED_MIN_ROI = "VARIANT_REACHED_MIN_ROI",
  FIRST_TO_MIN_ROI = "FIRST_TO_MIN_ROI",
  HIGHEST_ROI_AT_TIME = "HIGHEST_ROI_AT_TIME",
}

export enum AutosendOptionType {
  SEND_TO_FULL = "SEND_TO_FULL",
  SEND_TO_PORTION = "SEND_TO_PORTION",
}

// Required Date field that distinguishes between empty input (returns "Required")
// and unparseable input (falls back to zod's default error). errorMap alone can't
// make this distinction because zod's coerce runs before the errorMap sees the
// input — by then null / "" / undefined have all been coerced to an Invalid Date.
export const dateForForm = z
  .union([z.string(), z.number(), z.date(), z.null(), z.undefined()])
  .superRefine((v, ctx) => {
    if (v == null || v === "") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Required" });
    }
  })
  .pipe(
    z.coerce.date({
      errorMap: (_issue, ctx) => ({ message: ctx.defaultError }),
    })
  );

// What to do automatically after the evaluation succeeds and a winner is picked.
export const AutosendOptionSchema = z
  .discriminatedUnion("type", [
    z.object({ type: z.literal(AutosendOptionType.SEND_TO_FULL) }),
    z.object({
      type: z.literal(AutosendOptionType.SEND_TO_PORTION),
      textsToSend: z.number().int().min(1),
    }),
  ])
  .nullable()
  .default(null);

export type AutosendOption = z.infer<typeof AutosendOptionSchema>;

const BasePartialSendInputSchema = z.object({
  variantIds: z.array(z.string().uuid()).min(1),

  // Number of minutes to wait after sending to begin evaluating.
  evaluationDelayInMin: z.number().int().optional().default(0),

  // Exact time at which the evaluation window expires.
  expiresAt: z.coerce.date(),

  // Intended for evaluations that have an evaluationDelayInMin and are required to run only once.
  runOnce: z.boolean().optional().default(false),

  autosendOption: AutosendOptionSchema,
});

const BaseRoiDonationInputSchema = BasePartialSendInputSchema.extend({
  expiresAt: dateForForm,
  minRoi: z
    .number({ message: "Must be a number" })
    .nullish()
    .refine(v => v == null || v >= 0, { message: "Must be a positive value" })
    .transform(v => v ?? 0),
  minNumberOfDonations: z
    .number({ message: "Must be a number" })
    .int()
    .nullish()
    .refine(v => v == null || v >= 0, { message: "Must be a positive value" })
    .transform(v => v ?? 0),
  excludeDonationsOverInCents: z
    .number()
    .int()
    .nullish()
    .refine(v => v == null || v >= 0, { message: "Must be a positive value" }),
});

export const FirstToMinRoiCriteriaSchema = BaseRoiDonationInputSchema.extend({
  type: z.literal(CriteriaType.FIRST_TO_MIN_ROI),
});

export const HighestRoiAtTimeCriteriaBaseSchema =
  BaseRoiDonationInputSchema.extend({
    type: z.literal(CriteriaType.HIGHEST_ROI_AT_TIME),
  });

export const VariantReachedMinRoiCriteriaBaseSchema =
  BaseRoiDonationInputSchema.extend({
    type: z.literal(CriteriaType.VARIANT_REACHED_MIN_ROI),
    expiresAt: dateForForm.optional(),
  });

export const EvaluationCriteriaSchema = z
  .discriminatedUnion("type", [
    VariantReachedMinRoiCriteriaBaseSchema,
    FirstToMinRoiCriteriaSchema,
    HighestRoiAtTimeCriteriaBaseSchema,
  ])
  .transform(data => {
    if (data.type === CriteriaType.HIGHEST_ROI_AT_TIME) {
      return { ...data, runOnce: true, evaluationDelayInMin: 0 };
    }
    return data;
  });

export type EvaluationCriteriaInput = z.input<typeof EvaluationCriteriaSchema>;

export type EvaluationCriteriaOutput = z.infer<typeof EvaluationCriteriaSchema>;

// ---------------------------------------------------------------------------
// Cross-field validation (shared by the fe form and the backend on submit)
// ---------------------------------------------------------------------------

type CrossFieldErrors = Record<string, { type: string; message: string }>;

// The set of fields the cross-field rules need. Both the frontend (from the
// form values) and the backend (from the evaluation payload) build one of
// these and pass it in. `textingHours` is omitted by callers with no
// scheduling context (e.g. the immediate "start" mutation), which skips the
// schedule-required and within-hours rules.
export interface CrossFieldValues {
  type?: CriteriaType;
  scheduleFor?: Date | null;
  dontSendAfter?: Date;
  evaluationExpiresAt?: Date;
  evaluationDelayInMin?: number;
  minRoi?: number;
  minNumberOfDonations?: number | null;
  textingHours: { start: string; end: string; timezone: string };
}

function toDate(value?: string | number | Date | null): Date | null {
  if (value == null || value === "") {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

/**
 * Cross-field validations for the partial send form. These can't live in the
 * per-field Zod schemas -- an object-level `.refine`/`.superRefine` only runs
 * once every field already parses, so cross-field errors would disappear
 * whenever any single field is invalid. Instead they're computed here and
 * merged into the resolver result, which lets them both block submission and
 * surface as react-hook-form errors.
 *
 * Each error is attached to the real field path the user needs to change.
 * When several conflicts target the same field, the first one wins (added via
 * `addError`).
 */
export function validateAcrossFields(
  values: CrossFieldValues
): CrossFieldErrors {
  const errors: CrossFieldErrors = {};
  function addError(path: string, message: string) {
    if (!errors[path]) {
      errors[path] = { type: "validate", message };
    }
  }

  const scheduleFor = toDate(values.scheduleFor);
  const { textingHours } = values;

  if (
    textingHours &&
    !scheduleFor &&
    !isDateWithinTextingHours(new Date(), textingHours)
  ) {
    addError(
      "scheduleFor",
      "A scheduled send time is required outside of texting hours"
    );
  }

  if (
    textingHours &&
    scheduleFor &&
    !isDateWithinTextingHours(scheduleFor, textingHours)
  ) {
    addError("scheduleFor", "Must be within texting hours");
  }

  const dontSendAfter = toDate(values.dontSendAfter);
  const evaluationExpiresAt = toDate(values.evaluationExpiresAt);

  if (scheduleFor && dontSendAfter && scheduleFor >= dontSendAfter) {
    addError("dontSendAfter", "Must be after scheduled send time");
  }

  if (values.type === CriteriaType.FIRST_TO_MIN_ROI) {
    const delay = Number(values.evaluationDelayInMin) || 0;
    const evalRunsAt = new Date(
      (scheduleFor ?? new Date()).getTime() + delay * 60_000
    );
    if (evaluationExpiresAt && evalRunsAt >= evaluationExpiresAt) {
      addError(
        "evaluationExpiresAt",
        "Must be after start checking for a winner"
      );
    }
    if (
      scheduleFor &&
      evaluationExpiresAt &&
      scheduleFor >= evaluationExpiresAt
    ) {
      addError("evaluationExpiresAt", "Must be after scheduled send time");
    }
    if (
      evaluationExpiresAt &&
      dontSendAfter &&
      evaluationExpiresAt >= dontSendAfter
    ) {
      addError("dontSendAfter", "Must be after stop checking for a winner");
    }
  }

  if (values.type === CriteriaType.HIGHEST_ROI_AT_TIME) {
    if (
      scheduleFor &&
      evaluationExpiresAt &&
      scheduleFor >= evaluationExpiresAt
    ) {
      addError("evaluationExpiresAt", "Must be after scheduled send time");
    }
    if (
      evaluationExpiresAt &&
      dontSendAfter &&
      evaluationExpiresAt >= dontSendAfter
    ) {
      addError("dontSendAfter", "Must be after picking a winner");
    }
  }

  if (values.type === CriteriaType.VARIANT_REACHED_MIN_ROI) {
    const hasMinRoi = Number(values.minRoi) > 0;
    const hasMinDonations = Number(values.minNumberOfDonations) > 0;
    if (!hasMinRoi && !hasMinDonations) {
      addError(
        "minRoi",
        "Either a minimum ROI or minimum number of donations must be specified"
      );
    }
  }

  return errors;
}

// BACKEND-facing schema: the payload the schedule/start mutations persist.
// `expiresAt` is the campaign's "don't send after" time. Cross-field rules are
// enforced separately via validateAcrossFields since they can't be expressed
// inside this schema.
export const PartialSendEvaluationInputSchema = z.object({
  expiresAt: dateForForm,
  evaluation: EvaluationCriteriaSchema,
});

const RoiSingleVariantResultSchema = z.object({
  variantId: z.string().uuid(),
  roi: z.number(),
  donationCount: z.number(),
  reachedMinRoi: z.boolean(),
  excludedDonations: z.array(z.number().int()).default([]),
  initialCostInMillicents: z.number().optional().default(0),
  totalRaisedInCents: z.number().int().optional().default(0),
  totalSent: z.number().int().optional().default(0),
  variantOrder: z.number().int().nullable().optional().default(null),
  clickCount: z.number().int().optional().default(0),
  replyCount: z.number().int().optional().default(0),
  filterCount: z.number().int().optional().default(0),
  optOutCount: z.number().int().optional().default(0),
});

type RoiSingleVariantResult = z.infer<typeof RoiSingleVariantResultSchema>;

const RoiMultiVariantResultSchema = z.object({
  winningVariantId: z.string().uuid().optional(),
  variantResults: z.array(RoiSingleVariantResultSchema),
});

export const EvaluationResultSchema = z.discriminatedUnion("type", [
  z
    .object({ type: z.literal(CriteriaType.VARIANT_REACHED_MIN_ROI) })
    .merge(RoiSingleVariantResultSchema),
  z
    .object({ type: z.literal(CriteriaType.FIRST_TO_MIN_ROI) })
    .merge(RoiMultiVariantResultSchema),
  z
    .object({ type: z.literal(CriteriaType.HIGHEST_ROI_AT_TIME) })
    .merge(RoiMultiVariantResultSchema),
]);

export type EvaluationResult = z.infer<typeof EvaluationResultSchema>;

function buildExcludedDonationsMap(
  variantResults: RoiSingleVariantResult[]
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const vr of variantResults) {
    map[vr.variantId] = vr.excludedDonations.length;
  }
  return map;
}

const EvaluationSuccessEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(CriteriaType.VARIANT_REACHED_MIN_ROI),
    partialSendEvaluationId: z.string().uuid(),
    variantId: z.string().uuid(),
    roi: z.number(),
    excludedDonationsByScriptVariantId: z.record(z.string(), z.number()),
  }),
  z.object({
    type: z.literal(CriteriaType.FIRST_TO_MIN_ROI),
    partialSendEvaluationId: z.string().uuid(),
    bestScriptVariantId: z.string().uuid().optional(),
    bestScriptVariantRoi: z.number(),
    excludedDonationsByScriptVariantId: z.record(z.string(), z.number()),
  }),
  z.object({
    type: z.literal(CriteriaType.HIGHEST_ROI_AT_TIME),
    partialSendEvaluationId: z.string().uuid(),
    bestScriptVariantId: z.string().uuid().optional(),
    bestScriptVariantRoi: z.number(),
    excludedDonationsByScriptVariantId: z.record(z.string(), z.number()),
  }),
]);

export type EvaluationSuccessEvent = z.infer<
  typeof EvaluationSuccessEventSchema
>;

export function buildSuccessEvent(
  result: EvaluationResult,
  partialSendEvaluationId: string
): EvaluationSuccessEvent {
  if (result.type === CriteriaType.VARIANT_REACHED_MIN_ROI) {
    return EvaluationSuccessEventSchema.parse({
      type: result.type,
      partialSendEvaluationId,
      variantId: result.variantId,
      roi: result.roi,
      excludedDonationsByScriptVariantId: {
        [result.variantId]: result.excludedDonations.length,
      },
    });
  }

  return EvaluationSuccessEventSchema.parse({
    type: result.type,
    partialSendEvaluationId,
    bestScriptVariantId: result.winningVariantId,
    bestScriptVariantRoi: result.variantResults.reduce(
      (max, vr) => Math.max(max, vr.roi),
      0
    ),
    excludedDonationsByScriptVariantId: buildExcludedDonationsMap(
      result.variantResults
    ),
  });
}
